import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { keepSessionAlive } from '../api';

// What counts as the user being here. Not requests: the dashboard polls on its
// own, so only input from a person says the screen is attended.
const INPUT_EVENTS = ['mousedown', 'mousemove', 'keydown', 'wheel', 'touchstart'];

// How often input is reported, at most. A quarter of the session timeout, so
// several reports fit inside one timeout, kept between these bounds. Until the
// server has said what the timeout is, the shorter bound is used.
const LEAST_MS = 15 * 1000;
const MOST_MS = 5 * 60 * 1000;

export function reportEvery(timeoutSeconds) {
  if (!(timeoutSeconds > 0)) return LEAST_MS;
  return Math.min(MOST_MS, Math.max(LEAST_MS, (timeoutSeconds * 1000) / 4));
}

/**
 * Keeps a signed-in session alive while someone is using the dashboard: the
 * session times out after a period with no input, not a fixed time after
 * sign-in. Reports input to the server, which extends the session; the first
 * input after a quiet spell is reported at once, then at most once per
 * interval. An untouched screen reports nothing and signs out as before.
 * Renders nothing.
 */
export default function SessionKeepAlive({ enabled }) {
  useEffect(() => {
    if (!enabled) return undefined;

    let every = LEAST_MS;
    let lastReport = 0;
    let reporting = false;

    const onInput = () => {
      const now = Date.now();
      if (reporting || now - lastReport < every) return;
      reporting = true;
      lastReport = now;
      keepSessionAlive().then((timeoutSeconds) => {
        reporting = false;
        if (timeoutSeconds == null) {
          // Failed: try again on input soon, not a full interval from now.
          lastReport = now - every + LEAST_MS;
          return;
        }
        every = reportEvery(timeoutSeconds);
      });
    };

    const options = { capture: true, passive: true };
    INPUT_EVENTS.forEach((name) => window.addEventListener(name, onInput, options));
    return () => INPUT_EVENTS.forEach((name) => window.removeEventListener(name, onInput, options));
  }, [enabled]);

  return null;
}

SessionKeepAlive.propTypes = {
  enabled: PropTypes.bool
};
